import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, CheckSquare, GripVertical } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'

interface ChecklistItem {
  id: string
  text: string
  category: string | null
  sortOrder: number
  createdAt: string
}

const CATEGORIES = ['Equipment', 'Personal', 'Documents', 'Other'] as const

export function CrewChecklistPage() {
  const qc = useQueryClient()
  const [newText, setNewText] = useState('')
  const [newCategory, setNewCategory] = useState('')

  const { data, isLoading } = useQuery<ChecklistItem[], ApiClientError>({
    queryKey: ['crew-checklist'],
    queryFn: () => apiFetch('/crew/profile/checklist'),
  })

  const addMutation = useMutation({
    mutationFn: (input: { text: string; category?: string }) =>
      apiFetch('/crew/profile/checklist', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crew-checklist'] })
      setNewText('')
      setNewCategory('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/crew/profile/checklist/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crew-checklist'] }),
  })

  function handleAdd(e: FormEvent) {
    e.preventDefault()
    if (!newText.trim()) return
    const input: { text: string; category?: string } = { text: newText }
    if (newCategory) input.category = newCategory
    addMutation.mutate(input)
  }

  const items = data ?? []
  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = items.filter((i) => i.category === cat)
    return acc
  }, {} as Record<string, ChecklistItem[]>)
  const uncategorised = items.filter((i) => !i.category || !CATEGORIES.includes(i.category as any))

  return (
    <div>
      <PageHeader
        title="My Checklist"
        subtitle="Personal prep list — what to remember for every show"
      />

      {/* Add form */}
      <form onSubmit={handleAdd} className="mb-6 flex flex-wrap gap-2 rounded-lg border border-ovation-ink/10 bg-white p-4">
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add an item..."
          className="flex-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm min-w-[200px]"
        />
        <select
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          <option value="">No category</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button
          type="submit"
          disabled={addMutation.isPending || !newText.trim()}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-center text-sm text-ovation-muted">
          <CheckSquare className="mx-auto mb-2 h-8 w-8" />
          Your checklist is empty. Add items you need to remember for every show.
        </div>
      ) : (
        <div className="space-y-4">
          {CATEGORIES.map((cat) => {
            const catItems = grouped[cat] ?? []
            return catItems.length > 0 ? (
              <div key={cat}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">{cat}</h3>
                <div className="space-y-1">
                  {catItems.map((item) => (
                    <ChecklistRow key={item.id} item={item} onDelete={() => deleteMutation.mutate(item.id)} />
                  ))}
                </div>
              </div>
            ) : null
          })}
          {uncategorised.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">Other</h3>
              <div className="space-y-1">
                {uncategorised.map((item) => (
                  <ChecklistRow key={item.id} item={item} onDelete={() => deleteMutation.mutate(item.id)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ChecklistRow({ item, onDelete }: { item: ChecklistItem; onDelete: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-ovation-ink/10 bg-white px-3 py-2">
      <GripVertical className="h-4 w-4 shrink-0 text-ovation-muted/50" />
      <span className="flex-1 text-sm text-ovation-ink">{item.text}</span>
      <button
        type="button"
        onClick={onDelete}
        className="rounded p-1 text-ovation-muted hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
